/* eslint-disable max-len */
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Typography } from 'src/components/common/Typography';
import { EnumRoutePaths } from 'src/configs/routes';
import { makePath } from 'src/helpers/transformers';
import { allTagChecker, scrollToTop, setBodyOverflow } from 'src/helpers/utils';
import { useI18n } from 'src/hooks/use-i18n-translation';
import { useSessionRecorder } from 'src/hooks/use-session-recorder';
import { useBannerActions } from 'src/hooks/useBannerActions';
import { useGameAction } from 'src/hooks/useGameAction';
import { getPromotions } from 'src/services/cmsApi';
import { CmsPage } from 'src/types/cms/cmsCommonTypes';
import { EnumCmsPromotionPostTagsEnum, EnumCmsPromotionPostViewEnum } from 'src/types/cms/cmsEnums';
import { CmsPromotionPageFields, CmsPromotionPagesResponse } from 'src/types/cms/cmsPageTypes';
import { RootState } from 'src/types/store-types';
import CardWithInfoPage from './CardWithInfoPage';
import PromotionInfoPage from './PromotionInfoPage';
import PromotionModal from './PromotionModal';
import './promotions.scss';

const StaticPromotionPage: FC = () => {
  const { t }: Translation = useTranslation();
  const { locale } = useI18n();

  const sessionRecorder = useSessionRecorder();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { staticPathParam } = useParams();

  const { providerAction, loginAction, registrAction, promotionAction, categoryAction, externalUrlAction } =
    useBannerActions();

  const { handleActionGame } = useGameAction();

  const { selectedCategory } = useSelector((state: RootState) => state.app);
  const { categoriesData } = useSelector((state: RootState) => state.configs);
  const { tags } = useSelector((state: RootState) => state.games);

  const [tagList, setTagList] = useState<any[]>([]);

  const [promotions, setPromotions] = useState<CmsPromotionPagesResponse['result'] | null>(null);
  const [promotion, setPromotion] = useState<CmsPage<CmsPromotionPageFields> | null>(null);
  const [selectedTag, setSelectedTag] = useState<string>('all');
  const [showPromoModal, setShowPromoModal] = useState<boolean>(false);

  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const promotionTags = useMemo(() => {
    if (!promotions) return null;

    const targetArray = promotions.reduce((acc: EnumCmsPromotionPostTagsEnum[], cur) => {
      if (cur?.content?.pageFields.tags?.[0]) {
        acc.push(...cur?.content?.pageFields.tags.map((tag) => tag));
      }
      return acc;
    }, [] as EnumCmsPromotionPostTagsEnum[]);

    return allTagChecker(Array.from(new Set(targetArray)));
  }, [promotions]);

  const filteredPromotionContent = useMemo(() => {
    const promotionsWithContent = promotions?.filter((promotion) => promotion.content);

    const sortedPromotions = promotionsWithContent?.sort((a, b) => {
      const aValue = a.content?.pageFields?.priority ? +a.content.pageFields.priority : 0;
      const bValue = b.content?.pageFields?.priority ? +b.content.pageFields.priority : 0;
      return aValue - bValue;
    });

    if (!promotionTags || promotionTags.length <= 0 || selectedTag === 'all') return sortedPromotions;

    return sortedPromotions?.filter((promotion) =>
      promotion.content?.pageFields.tags?.some((m: string) => m === selectedTag)
    );
  }, [promotions, selectedTag, promotionTags]);

  const handleClick = (promotion: CmsPage<CmsPromotionPageFields>): void => {
    sessionRecorder?.trackEvent('Promotion card click', {
      promotionName: promotion.content?.pageFields.cardTitle,
    });

    const { externalContentUrl, viewType } = promotion.content?.pageFields ?? {};

    const slugPath = `${EnumRoutePaths.PROMOTIONS}/${makePath(promotion.slug)}`;

    if (externalContentUrl && viewType === EnumCmsPromotionPostViewEnum.NEW_PAGE) {
      window.open(externalContentUrl);
      return;
    }

    setPromotion(promotion);
    navigate(slugPath);

    if (!externalContentUrl) {
      return;
    }

    if (viewType === EnumCmsPromotionPostViewEnum.DIALOG) {
      setShowPromoModal(true);
      setBodyOverflow('unset');
      return;
    }

    // INLINE or default
    setShowPromoModal(false);
    setBodyOverflow('set');
  };

  const closePromoModal = (): void => {
    setBodyOverflow('set');
    setShowPromoModal(false);
    navigate(EnumRoutePaths.PROMOTIONS);
    setPromotion(null);
  };

  const handleBack = (): void => {
    navigate(EnumRoutePaths.PROMOTIONS);
    setPromotion(null);
  };

  const handleSelectetTag = (tag: string): void => {
    setSelectedTag(tag);
  };

  const redirect = (): void => {
    const variant = promotion?.content?.pageFields?.cta?.variant;
    const param = promotion?.content?.pageFields?.cta?.param;

    switch (variant) {
      case 'goToRegister':
        registrAction();
        break;
      case 'goToLogin':
        loginAction();
        break;
      case 'goToPromotion':
      case 'goToPromotions':
        promotionAction(param);
        break;
      case 'goToProvider':
        providerAction({ provider: param, tags: tags, tagList: tagList });
        break;
      case 'goToExternalUrl':
        externalUrlAction(param);
        break;
      case 'goToGame':
        handleActionGame(param as number);
        break;
      case 'goToCategory':
        categoryAction(param);
        break;
      case 'goToRace':
      case 'noAction':
        break;
      default:
        return;
    }
  };

  const getPromotionData = (): void => {
    getPromotions(locale).then((res) => setPromotions(res.result));
  };

  useEffect(() => {
    scrollToTop();
  }, [promotion]);

  useEffect(() => {
    getPromotionData();
  }, [locale]);

  useEffect(() => {
    categoriesData &&
      setTagList(categoriesData[selectedCategory]?.tags?.filter((f) => f.tagName === 'allGames')?.[0].tagGames || []);
  }, [categoriesData]);

  useEffect(() => {
    if (staticPathParam && !!promotions?.length) {
      const promo = promotions?.find((promotion) => makePath(promotion.slug) === staticPathParam);
      if (promo) {
        setPromotion(promo);
        return;
      }
      const target = EnumRoutePaths.PROMOTIONS;
      if (target !== window.location.pathname) {
        navigate(target, { replace: true });
      }
    }
  }, [staticPathParam, promotions, searchParams, navigate]);

  useEffect(() => {
    sessionRecorder?.trackEvent('Promotions page view');
  }, []);

  if (!filteredPromotionContent) return null;

  return (
    <div className="promotions__container">
      {!promotion && (
        <div className="promotions__inner">
          <Typography className="promotions__title" variant={'h6'}>
            {t('promotions')}
          </Typography>
          {promotionTags && promotionTags?.length > 0 && (
            <div className="promotions__tags">
              {promotionTags.map((tag: string) => (
                <div
                  key={tag}
                  onClick={handleSelectetTag.bind(null, tag)}
                  className={`promotions__tag ${tag === selectedTag ? 'promotions__tag--active' : ''}`}
                >
                  {t(tag) ?? tag}
                </div>
              ))}
            </div>
          )}
          <div className="promotions__bonus-list">
            {filteredPromotionContent?.map((cardItem) => (
              <CardWithInfoPage key={cardItem.id} cardInfo={cardItem} onBtnClick={handleClick} />
            ))}
          </div>
        </div>
      )}
      {showPromoModal && (
        <PromotionModal externalUrl={promotion?.content?.pageFields.externalContentUrl || ''} close={closePromoModal} />
      )}

      {promotion && !showPromoModal && (
        <PromotionInfoPage handleBack={handleBack} handleRedirect={redirect} promotionPageInfo={promotion} />
      )}
    </div>
  );
};
export default StaticPromotionPage;
